class AboutHeaderSearch extends HTMLElement {
  #events;
  #request;
  #timer;
  #frame;
  #observer;
  #input;
  #panel;
  #results;
  #status;
  #heading;
  #clear;
  #fallback;
  #template;
  #field;
  #header;
  #page;
  #opening = false;
  #openSequence = 0;
  #open = false;
  #suppressFocus = false;
  #sequence = 0;
  #selected = -1;
  #loadedQuery = null;
  #cache = new Map();

  connectedCallback() {
    if (this.#events) return;
    this.#input = this.querySelector('[data-search-input]');
    this.#panel = this.querySelector('[data-search-panel]');
    this.#results = this.querySelector('[data-search-results-container]');
    this.#status = this.querySelector('[data-search-status]');
    this.#heading = this.querySelector('[data-search-heading]');
    this.#clear = this.querySelector('[data-search-clear]');
    this.#fallback = this.querySelector('[data-search-fallback]');
    this.#template = this.querySelector('template');
    this.#field = this.querySelector('[data-search-field]');
    this.#page = this.closest('.page-wrapper');
    this.#header = this.closest('header-component');
    if (!this.#input || !this.#panel || !this.#results || !this.#template) return;

    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    this.querySelector('form').addEventListener('submit', (event) => {
      if (!this.#input.value.trim()) event.preventDefault();
    }, options);
    this.#input.addEventListener('focus', this.#handleFocus, options);
    this.#input.addEventListener('click', this.#handleFocus, options);
    this.#input.addEventListener('input', this.#handleInput, options);
    this.#input.addEventListener('compositionend', this.#handleInput, options);
    this.addEventListener('keydown', this.#handleKey, options);
    this.addEventListener('focusout', this.#handleFocusOut, options);
    this.#results.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button === 0 && event.target.closest('a[href]')) {
        event.preventDefault();
      }
    }, options);
    this.#results.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !this.#results.contains(link)) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(link.href);
    }, options);
    this.#clear.addEventListener('click', this.#clearSearch, options);
    this.querySelector('[data-search-close]').addEventListener('click', () => this.#close(true), options);
    document.addEventListener('pointerdown', this.#handleOutside, { ...options, capture: true });
    document.addEventListener('pointerover', this.#handleMenuHover, options);
    document.addEventListener('theme-drawer:open', () => this.#close(), options);
    document.addEventListener('scroll', this.#schedulePosition, { ...options, capture: true, passive: true });
    window.addEventListener('resize', this.#schedulePosition, options);
    this.#observer = new ResizeObserver(this.#schedulePosition);
    this.#observer.observe(this.#field);
    if (this.#header) this.#observer.observe(this.#header);
    this.#renderEmpty();
  }

  disconnectedCallback() {
    this.#close();
    this.#events?.abort();
    this.#events = undefined;
    this.#cancelRequest();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    this.#page?.classList.remove('page-wrapper--search-open');
  }

  #handleFocus = () => {
    if (this.#suppressFocus) return;
    this.#show();
    const query = this.#input.value.trim();
    if (query && query !== this.#loadedQuery && !this.#request) this.#search(query);
  };

  #show() {
    if (this.#open || this.#opening) return;
    const drawers = Array.from(document.querySelectorAll('theme-drawer[open]')).filter(drawer => typeof drawer.close === 'function');
    if (drawers.length) {
      this.#opening = true;
      const sequence = ++this.#openSequence;
      Promise.all(drawers.map(drawer => drawer.close())).then(() => {
        if (!this.#opening || sequence !== this.#openSequence || !this.isConnected) return;
        this.#opening = false;
        this.#input.focus({ preventScroll: true });
        this.#show();
      }).catch(() => {
        if (sequence === this.#openSequence) this.#opening = false;
      });
      return;
    }
    if (!this.#position()) return;
    this.#panel.showPopover?.({ source: this.#input });
    this.#panel.setAttribute('data-open', '');
    this.#open = true;
    this.#page?.classList.toggle('page-wrapper--search-open', this.#panel.hasAttribute('data-docked'));
    this.#input.setAttribute('aria-expanded', 'true');
  }

  #close(restoreFocus = false) {
    this.#opening = false;
    this.#openSequence += 1;
    this.#page?.classList.remove('page-wrapper--search-open');
    if (!this.#open) return;
    this.#open = false;
    this.#panel.hidePopover?.();
    this.#panel.removeAttribute('data-open');
    this.#input.setAttribute('aria-expanded', 'false');
    this.#select(-1);
    this.#cancelRequest();
    if (restoreFocus) {
      this.#suppressFocus = true;
      this.#input.focus({ preventScroll: true });
      this.#suppressFocus = false;
    }
  }

  #handleOutside = (event) => {
    if (!this.contains(event.target)) this.#close();
  };

  #handleMenuHover = (event) => {
    if (event.target instanceof Element && event.target.closest('header-menu')) this.#close();
  };

  #handleFocusOut = () => {
    queueMicrotask(() => {
      if (!this.#opening && !this.contains(document.activeElement)) this.#close();
    });
  };

  #schedulePosition = (event) => {
    if (!this.#open || this.#frame) return;
    if (event?.target && this.#panel.contains(event.target)) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      if (!this.#position()) this.#close();
    });
  };

  #position() {
    const rect = this.#field.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    if (!rect.width || rect.bottom <= 0 || rect.bottom > viewportHeight - 120) return false;
    const docked = viewportWidth >= 990;
    const headerBottom = this.#header?.getBoundingClientRect().bottom ?? rect.bottom;
    const top = Math.round(headerBottom);
    if (top <= 0 || top > viewportHeight - 120) return false;
    const width = docked
      ? Math.min(800, Math.max(420, Math.round(420 + (viewportWidth - 990) * (380 / 290))))
      : Math.min(420, Math.round(viewportWidth * 0.55));
    const left = viewportWidth - width;
    this.#panel.toggleAttribute('data-docked', true);
    this.#page?.style.setProperty('--about-search-dock-width', docked ? width + 'px' : '0px');
    this.#page?.classList.toggle('page-wrapper--search-open', docked && this.#open);
    this.#panel.style.setProperty('--search-panel-left', left + 'px');
    this.#panel.style.setProperty('--search-panel-top', top + 'px');
    this.#panel.style.setProperty('--search-panel-width', width + 'px');
    this.#panel.style.setProperty('--search-panel-height', Math.max(120, viewportHeight - top) + 'px');
    return true;
  }

  #cancelRequest() {
    clearTimeout(this.#timer);
    this.#request?.abort();
    this.#request = undefined;
    this.#sequence += 1;
    this.#results?.removeAttribute('aria-busy');
  }

  #handleInput = (event) => {
    this.#cancelRequest();
    this.#select(-1);
    this.#clear.hidden = !this.#input.value.length;
    this.#show();
    if (event.isComposing) return;
    const query = this.#input.value.trim();
    if (!query) {
      this.#renderEmpty();
      return;
    }
    this.#heading.textContent = this.dataset.resultsLabel;
    this.#status.hidden = false;
    this.#status.textContent = this.dataset.loadingLabel;
    this.#fallback.hidden = true;
    this.#results.setAttribute('aria-busy', 'true');
    this.#results.replaceChildren();
    this.#loadedQuery = null;
    this.#timer = setTimeout(() => this.#search(query), 200);
  };

  #searchURL(query) {
    const url = new URL(this.querySelector('form').action, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('options[unavailable_products]', 'hide');
    return url;
  }

  async #search(query) {
    this.#cancelRequest();
    const sequence = this.#sequence;
    const controller = new AbortController();
    this.#request = controller;
    this.#heading.textContent = this.dataset.resultsLabel;
    this.#status.hidden = false;
    this.#status.textContent = this.dataset.loadingLabel;
    this.#fallback.hidden = true;
    this.#results.setAttribute('aria-busy', 'true');
    try {
      let markup = this.#cache.get(query);
      if (!markup) {
        const url = new URL(this.dataset.predictiveUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'product,collection,page,article,query');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][fields]', 'title,body');
        url.searchParams.set('resources[options][unavailable_products]', 'hide');
        const previewId = new URL(location.href).searchParams.get('preview_theme_id');
        if (previewId) url.searchParams.set('preview_theme_id', previewId);
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Search request failed');
        markup = await response.text();
      }
      if (controller.signal.aborted || sequence !== this.#sequence || this.#input.value.trim() !== query) return;
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const results = parsed.getElementById('predictive-search-results');
      if (!results) throw new Error('Search results missing');
      this.#mount(results);
      this.#loadedQuery = query;
      this.#cache.set(query, markup);
      if (this.#cache.size > 20) this.#cache.delete(this.#cache.keys().next().value);
      this.#status.hidden = true;
    } catch (error) {
      if (controller.signal.aborted || sequence !== this.#sequence) return;
      this.#results.replaceChildren();
      this.#loadedQuery = null;
      this.#status.textContent = this.dataset.errorLabel;
      this.#status.hidden = false;
      this.#fallback.href = this.#searchURL(query).toString();
      this.#fallback.hidden = false;
    } finally {
      if (sequence === this.#sequence) {
        this.#results.removeAttribute('aria-busy');
        this.#request = undefined;
      }
    }
  }

  #mount(root) {
    const prefix = this.id + '-';
    const nodes = [root, ...root.querySelectorAll('*')];
    for (const node of nodes) {
      if (node.id) node.id = prefix + node.id;
      for (const attribute of ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns', 'for']) {
        if (!node.hasAttribute(attribute)) continue;
        node.setAttribute(attribute, node.getAttribute(attribute).split(/\s+/).map(id => prefix + id).join(' '));
      }
      node.removeAttribute('on:keydown');
    }
    this.#results.replaceChildren(root);
    this.#select(-1);
    this.#results.scrollTop = 0;
  }

  #renderEmpty() {
    const root = this.#template.content.querySelector('.predictive-search-dropdown')?.cloneNode(true);
    if (root) this.#mount(root);
    this.#heading.textContent = this.dataset.suggestionsLabel;
    this.#status.hidden = true;
    this.#fallback.hidden = true;
    this.#clear.hidden = !this.#input.value.length;
    this.#loadedQuery = '';
  }

  #clearSearch = () => {
    this.#cancelRequest();
    this.#input.value = '';
    this.#renderEmpty();
    this.#input.focus({ preventScroll: true });
    this.#show();
  };

  get #items() {
    return Array.from(this.#results.querySelectorAll('[ref="resultsItems[]"]'));
  }

  #select(index) {
    const items = this.#items;
    this.#selected = index;
    this.#input.removeAttribute('aria-activedescendant');
    items.forEach((item, itemIndex) => {
      item.classList.toggle('keyboard-focus', itemIndex === index);
      if (itemIndex !== index) {
        item.removeAttribute('aria-selected');
        return;
      }
      item.id ||= this.id + '-option-' + itemIndex;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'true');
      this.#input.setAttribute('aria-activedescendant', item.id);
      item.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }

  #handleKey = (event) => {
    if (event.isComposing) return;
    if (event.key === 'Escape' && (this.#open || this.#opening)) {
      event.preventDefault();
      event.stopPropagation();
      this.#close(true);
      return;
    }
    if (event.target !== this.#input) return;
    if (event.key === 'Tab') {
      if (this.#opening) this.#close();
      this.#select(-1);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      this.#show();
      const items = this.#items;
      if (!items.length) return;
      event.preventDefault();
      const next = event.key === 'ArrowDown'
        ? (this.#selected + 1) % items.length
        : (this.#selected <= 0 ? items.length - 1 : this.#selected - 1);
      this.#select(next);
    } else if (event.key === 'Enter' && this.#open && this.#selected >= 0) {
      event.preventDefault();
      this.#items[this.#selected]?.querySelector('a')?.click();
    }
  };
}

if (!customElements.get('about-header-search')) {
  customElements.define('about-header-search', AboutHeaderSearch);
}
